import { InputHTMLAttributes, useEffect, useRef, useState } from 'react'
import {
  FieldValues,
  UseFormRegister,
  Path,
  FieldErrors,
  RegisterOptions,
} from 'react-hook-form'
import { isEmpty, noop } from 'lodash'
import {
  SvgCross,
  SvgDownloadIcon,
  SvgDrag,
  SvgUpload,
} from '../../assets/images'
import {
  checkFileExtensionPresent,
  FILE_NAME_VALIDATION_ERRORS,
  FILE_TYPE_TOASTER,
  getFileExtension,
  getUnsupportedFileTypeMessage,
  isFileSignatureConsistent,
  NAMESPACE,
  validateFileName,
} from '../../utils'
import Tooltip from '../Tooltip'
import {
  FileDataFromExtraResourceSingleUpload,
  MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE,
  ToasterTypeValues,
} from '../../utils/types/fileUploadTypes'
import FileTypeComponentIcon from './FileTypeComponentIcon'
import {
  detectPasswordProtectedFile,
  MICROSITE_PASSWORD_HINT,
  PASSWORD_PROTECTED_TOASTER_MSG,
} from './passwordProtectedFileCheck'
import type { PasswordProtectionPlatform } from './passwordProtectedFileCheck'
import Draggable from 'react-draggable'
import { v4 as uuid } from 'uuid'
interface FileInputPropTypes<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  name: Path<T>
  label?: string
  register?: UseFormRegister<T>
  errors?: FieldErrors<T>
  className?: string
  validation?: RegisterOptions<T, Path<T>>
  commentLabel?: string
  accept?: string
  comment?: string
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onFileClear?: (event: React.MouseEvent<HTMLDivElement>) => void
  defaultValue?: string | number
  previewUrl?: string
  isRequired?: boolean
  labelClass?: string
  commentLabelClass?: string
  dataQaId?: string
  toolTip?: string
  handleToaster?: (message: string, type: ToasterTypeValues) => void
  maxSizeOfFile?: string
  isPdfFileInput?: boolean
  downloadClick?: (previewUrl: string, fileName: string) => void
  isOpenFromElementRenderer?: boolean
  defaultFileElementRenderer?: FileDataFromExtraResourceSingleUpload
  handleDownloadOnFileNameClick?: () => void
  confirmationModal?: (value: string | null) => Promise<boolean | undefined>
  customStyles?: object
  showRequiredError?: boolean
  disablePasswordProtection?: boolean
  passwordProtectionPlatform?: PasswordProtectionPlatform
  showStaticMicrositePasswordHint?: boolean
}

const FileInput = <T extends FieldValues>({
  name,
  label,
  disabled = false,
  placeholder = '',
  register,
  errors,
  validation,
  className = '',
  commentLabel = '',
  comment = '',
  accept = '.png, .jpeg',
  readOnly = false,
  onChange = noop,
  onFileClear = noop,
  defaultValue = '',
  previewUrl = '',
  isRequired,
  labelClass = 'text-xs',
  commentLabelClass = 'text-xs',
  dataQaId,
  toolTip,
  handleToaster,
  maxSizeOfFile,
  isPdfFileInput = false,
  downloadClick,
  isOpenFromElementRenderer,
  defaultFileElementRenderer,
  handleDownloadOnFileNameClick,
  confirmationModal,
  customStyles = {},
  showRequiredError = true,
  disablePasswordProtection = false,
  passwordProtectionPlatform = 'neoengage',
  showStaticMicrositePasswordHint = true,
  ...rest
}: FileInputPropTypes<T>) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const previewDragHandleClassRef = useRef(`drag-handle-${uuid()}`)
  const previewDragHandleClass = previewDragHandleClassRef.current
  const addJpegIfJpgExists = (fileTypes: string) => {
    if (fileTypes.includes('.jpg') && !fileTypes.includes('.jpeg')) {
      return fileTypes + ', .jpeg'
    }
    return fileTypes
  }

  const updatedFileTypes = addJpegIfJpgExists(accept)
  const {
    ref: registerRef,
    onChange: fromOnchange,
    ...registerRest
  } = register?.(name, {
    ...validation,
    validate: (value) => {
      if (!isRequired) return true
      if (fileName) return true
      return value && value.length > 0 ? true : NAMESPACE?.REQUIRED_FIELD
    },
  }) || {}
  const [fileName, setFileName] = useState<string | number>(defaultValue)
  const [isFile, setIsFile] = useState<boolean>(!!defaultValue)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [isPreview, setIsPreview] = useState<boolean>(false)
  const [fileUploadedData, setFileUploadedData] = useState<File | null>(null)

  useEffect(() => {
    if (defaultValue) {
      setFileName(defaultValue)
      setIsFile(true)
      setFilePreview(previewUrl || null)
    } else {
      if (isPdfFileInput && defaultValue === '') {
        setFileName('')
        setIsFile(false)
        setFilePreview(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
          fromOnchange?.({
            target: {
              name,
              value: null,
            },
          })
        }
      }
      setFilePreview(null)
    }
  }, [defaultValue, previewUrl])

  const previewFile = (file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setFilePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    const selectedFileList = Array.from(files ?? [])
    if (selectedFileList.length === 0) {
      return
    }
    const selectedFile = selectedFileList[0]
    const fileExtension = getFileExtension(selectedFile.name)
    const acceptedExtensions = updatedFileTypes
      ?.split(', ')
      ?.map((val) => val.toLowerCase())
    const hasValidSignature = await isFileSignatureConsistent(selectedFile)
    const passwordProtectionResult = await detectPasswordProtectedFile(
      selectedFile
    )
    const isPasswordProtected = passwordProtectionResult === 'blocked_password'
    const fileNameValidationResult = validateFileName(selectedFile.name)

    if (!checkFileExtensionPresent(selectedFile.name)) {
      const message = `${FILE_NAME_VALIDATION_ERRORS.NO_EXTENSION_FOUND} ${updatedFileTypes}`
      handleToaster && handleToaster(message, 'error')
      event.target.value = ''
      return
    } else if (!acceptedExtensions.includes(fileExtension)) {
      const message = getUnsupportedFileTypeMessage(
        fileExtension,
        updatedFileTypes
      )
      handleToaster && handleToaster(message, 'error')
      event.target.value = ''
      return
    } else if (isPasswordProtected) {
      handleToaster?.(PASSWORD_PROTECTED_TOASTER_MSG, 'error')
      event.target.value = ''
      return
    } else if (!hasValidSignature) {
      handleToaster &&
        handleToaster(NAMESPACE.INVALID_FILE_SIGNATURE_TOASTER_MSG, 'error')
      event.target.value = ''
      return
    } else if (
      selectedFile?.size / 1024 >
      (maxSizeOfFile ? +maxSizeOfFile : 5120)
    ) {
      const message = `File [${
        selectedFile.name
      }] size exceed the maximum limit of ${maxSizeOfFile ?? 5120} KB.`
      handleToaster && handleToaster(message, 'error')
      event.target.value = ''
      return
    } else if (!fileNameValidationResult.isValid) {
      handleToaster &&
        handleToaster(fileNameValidationResult.error || '', 'error')
      event.target.value = ''
      return
    }
    onChange(event)
    if (files && files.length > 0) {
      const selectedFile = files[0]
      setFileName(selectedFile.name)
      setIsFile(true)
      previewFile(selectedFile)
      setFileUploadedData(selectedFile)

      fromOnchange?.({
        target: {
          name,
          value: selectedFile,
        },
      })
    }
  }

  const handleClearFile = async (event: React.MouseEvent<HTMLDivElement>) => {
    if (confirmationModal) {
      const confirmed = await confirmationModal(null)
      if (!confirmed) return
    }
    if (
      isOpenFromElementRenderer &&
      defaultFileElementRenderer &&
      !fileUploadedData
    ) {
      const delay = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms))
      await delay(1500)
    }
    event.stopPropagation()
    event.preventDefault()
    setFileName('')
    setFileUploadedData(null)
    setIsFile(false)
    setFilePreview(null)
    onFileClear?.(event)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fromOnchange?.({
        target: {
          name,
          value: isOpenFromElementRenderer
            ? { id: defaultFileElementRenderer?.id, _state: 'REMOVED' }
            : null,
        },
      })
    }
  }

  const handlePreview = () => {
    if (handleDownloadOnFileNameClick) {
      handleDownloadOnFileNameClick()
      return
    }
    if (isOpenFromElementRenderer) {
      if (
        (defaultFileElementRenderer?.file_type?.includes('application') &&
          !defaultFileElementRenderer?.file_type?.includes('pdf') &&
          defaultFileElementRenderer &&
          !fileUploadedData) ||
        (fileUploadedData &&
          fileUploadedData?.type?.includes('application') &&
          !fileUploadedData?.type?.includes('pdf'))
      ) {
        handleToaster &&
          handleToaster(FILE_TYPE_TOASTER.FILE_TYPE_NOT_SUPPORTED, 'error')
        return
      }
    }

    setIsPreview((prev) => !prev)
  }

  return (
    <div
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (!isFile && !readOnly && !disabled) {
            handleUploadFile()
          }
        }
      }}
      className={` group   relative w-full font-primary  ${className}   ${
        disabled ? ' cursor-not-allowed' : 'cursor-pointer select-none'
      }`}
      style={{ ...customStyles }}
      onClick={isFile || readOnly || disabled ? noop : handleUploadFile}
    >
      <Tooltip content={toolTip ?? ''}>
        <div className=" max-h-[40px]  w-full box-border relative bg-accent-0 items-center rounded">
          {label ? (
            <div
              className={`translate-x-[0.875rem] translate-y-[-0.75rem] flex  text-base p-0 origin-top-left truncate max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-[1] pointer-events-none ${
                disabled ? 'text-accent-300' : 'text-black'
              }`}
            >
              <label
                className={` font-extraLight block leading-1 font-primary truncate max-w-[calc(100% - 1.5rem]  

            ${
              disabled ? 'text-accent-300' : 'group-hover:text-secondary-500'
            } ${labelClass}`}
                title={label}
                style={{ ...customStyles }}
              >
                {label}
              </label>
              {commentLabel ? (
                <span
                  className={`ml-1 leading-1 truncate max-w-[calc(100% - 1.5rem] text-accent-300 ${commentLabelClass}`}
                >
                  {commentLabel}
                </span>
              ) : null}
              {isRequired ? (
                <span
                  className={`ml-1 text-xs leading-1  ${
                    disabled ? 'text-accent-300' : 'text-error-500'
                  }`}
                >
                  *
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="z-[2] pl-4 pr-4 pt-2 pb-2 max-h-[40px]  relative">
            <div className="flex  items-center justify-between w-full  ">
              <div
                className={`font-light max-w-[calc(100% - 15px)] w-full overflow-y-auto overflow-x-hidden no-scroll-display items-center h-[24px] flex flex-wrap gap-1
              ${disabled ? 'text-accent-500' : ''}
          ${!isEmpty(fileName) ? 'text-black' : 'text-accent-800'}`}
                onClick={isFile ? handlePreview : noop}
              >
                <input
                  type="file"
                  accept={updatedFileTypes}
                  {...rest}
                  {...registerRest}
                  {...(register ? register(name, { ...validation }) : { name })}
                  ref={(e) => {
                    registerRef?.(e)
                    fileInputRef.current = e
                  }}
                  onChange={handleFileChange}
                  className="hidden"
                  data-qa-id={dataQaId ?? ''}
                />

                <span
                  className={`leading-5 text-xs truncate max-w-[calc(100%-15px)] w-full ${
                    isFile ? 'text-secondary-500 font-light underline' : ''
                  } ${
                    isOpenFromElementRenderer && isFile
                      ? 'flex flex-column items-center'
                      : ''
                  }`}
                  onClick={noop}
                >
                  {isOpenFromElementRenderer && isFile ? (
                    <div className="pr-2 ">
                      {
                        <FileTypeComponentIcon
                          fileType={
                            fileName === defaultFileElementRenderer?.file_name
                              ? (defaultFileElementRenderer?.file_type as MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE)
                              : (fileUploadedData?.type as MULTI_UPLOAD_FILE_TYPE_VALUES_TYPE)
                          }
                          smallIcon={true}
                        />
                      }
                    </div>
                  ) : null}
                  {isFile ? fileName : placeholder}
                </span>
              </div>
              {downloadClick &&
              isFile &&
              fileName === defaultFileElementRenderer?.file_name &&
              !fileUploadedData ? (
                <div
                  role="button"
                  tabIndex={rest?.tabIndex ?? 0}
                  className="p-2 cursor-pointer"
                  onClick={() =>
                    downloadClick(previewUrl, defaultValue as string)
                  }
                >
                  <SvgDownloadIcon />
                </div>
              ) : null}
              {isFile && !readOnly ? (
                <div
                  className={`p-1 mr-3 ${
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  onClick={disabled ? noop : handleClearFile}
                >
                  <SvgCross />
                </div>
              ) : null}

              {!isFile && !readOnly ? (
                <div
                  role="button"
                  tabIndex={rest?.tabIndex ?? 0}
                  className="p-2 cursor-pointer"
                  onClick={noop}
                >
                  <SvgUpload />
                </div>
              ) : null}
            </div>
          </div>

          <fieldset
            aria-hidden="true"
            className={`text-left  border absolute ${
              label ? 'top-[-15px]' : 'top-0'
            } bottom-0 left-0 right-0 px-2 pointer-events-none rounded border-solid overflow-hidden min-w-0 
                    border-border-150
                  ${
                    disabled
                      ? 'border-border-200 bg-accent-50'
                      : 'group-hover:border-secondary-500'
                  }
                  ${
                    !isEmpty(errors?.[name])
                      ? 'border-error-500'
                      : 'border-border-150'
                  }`}
          >
            {label ? (
              <legend className=" invisible flex float-none max-w-full select-none  p-1 box-border ">
                <span
                  className={` font-extraLight  dropdown-label overflow-hidden whitespace-nowrap  text-ellipsis  

                      ${
                        disabled
                          ? 'text-accent-300'
                          : 'group-hover:text-secondary-500 '
                      }`}
                >
                  {label}
                </span>
                {commentLabel ? (
                  <span
                    className={`ml-1 text-xs leading-1 truncate max-w-[calc(100% - 1.5rem] text-accent-300 font-primary`}
                    style={{ ...customStyles }}
                  >
                    {commentLabel}
                  </span>
                ) : null}
                {validation?.required ? (
                  <span
                    className={`ml-1 text-xs leading-1  ${
                      disabled ? 'text-accent-300' : 'text-error-500'
                    }`}
                  >
                    *
                  </span>
                ) : null}
              </legend>
            ) : null}
          </fieldset>
        </div>
      </Tooltip>
      {isPreview ? (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[99]">
          <Draggable handle={`.${previewDragHandleClass}`} bounds="body">
            <div className="relative bg-accent-0 p-2 pt-6">
              <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10">
                <button
                  type="button"
                  className={`${previewDragHandleClass} cursor-move`}
                >
                  <SvgDrag className="rotate-90" />
                </button>
              </div>
              <button
                className="absolute px-2 pb-2 h-8 w-8 top-0 right-0 text-base"
                onClick={handlePreview}
              >
                <SvgCross />
              </button>
              {isOpenFromElementRenderer ? (
                <embed
                  src={filePreview ?? ''}
                  width="700rem"
                  height="700rem"
                  className="max-w-full max-h-[80vh] object-contain"
                />
              ) : (
                <img
                  src={filePreview ?? ''}
                  alt="Preview"
                  className="h-[30rem] w-full object-cover"
                />
              )}
            </div>
          </Draggable>
        </div>
      ) : null}
      {comment ? <span className="text-black text-xs">{comment}</span> : null}
      {showStaticMicrositePasswordHint &&
      !disablePasswordProtection &&
      passwordProtectionPlatform === 'microsite' ? (
        <span className="block text-xs text-accent-600 mt-0.5">
          {MICROSITE_PASSWORD_HINT}
        </span>
      ) : null}

      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === 'required' &&
        showRequiredError && (
          <span
            className="text-error-500 text-xxs font-primary font-extraLight"
            style={{ ...customStyles }}
          >
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
      {errors &&
        name &&
        errors?.[name]?.type === 'validate' &&
        showRequiredError && (
          <span
            className="text-error-500 text-xxs font-primary font-normal"
            style={{ ...customStyles }}
          >
            {typeof errors?.[name]?.message === 'string'
              ? errors?.[name]?.message?.toString()
              : 'Invalid file'}
          </span>
        )}
      {errors &&
        name &&
        errors?.[name] &&
        errors?.[name]?.['type'] === 'pattern' && (
          <span
            className="text-error-500 text-xs font-primary"
            style={{ ...customStyles }}
          >
            {errors?.[name]?.['message']?.toString()}
          </span>
        )}
    </div>
  )
}

export default FileInput
